import dayjs from 'dayjs';
import API from './API';
import { normalize } from './crud';

// -----------------------------------------------------------------------------
//  NUOVO CLIENTE — le chiamate della pagina di aggiunta cliente.
//
//  Ogni chiamata passa `skipErrorToast`: il messaggio lo decide la pagina,
//  che sa cosa significa l'errore in quel punto (un 409 sulla registrazione
//  e' «email gia' usata», non un generico «conflitto»).
//
//  ⚠️ LE AUTO VIAGGIANO DENTRO LA REGISTRAZIONE, non con `POST /car`.
//  `customer.cars` è in cascade, quindi cliente e auto si salvano insieme e
//  la risposta contiene già le auto con il loro id.
//
//  ⚠️ PACCHETTO VALET = entita' `Minute`, legata all'AUTO. Qui si usa il
//  modello NUOVO: `valet_per_year` + `duration_years`, con il consumo
//  addebitato a fasce km (`valet_km_band`) e il prezzo del pacchetto pagato a
//  rate. Le colonne `partner.one/two/three_valet` sono deprecate.
// -----------------------------------------------------------------------------

export const IVA = 0.22;

const SILENZIOSO = { skipErrorToast: true };

const arrotonda = (n) => Math.round(n * 100) / 100;

/**
 * Il partner dell'utente e le sue sedi: servono alle auto (`car.headquarters`).
 */
export async function contestoPartner(employeeId) {
  const res = await API.get('/employee', {
    ...SILENZIOSO,
    params: {
      filter: `id||$eq||${employeeId}`,
      join: ['headquarters', 'headquarters.partner'],
    },
  });
  const sedi = normalize(res).rows[0]?.headquarters || [];
  return { sedi, partnerId: sedi[0]?.partner?.id || null };
}

/**
 * L'email appartiene gia' a un cliente Simba, di qualunque partner?
 *
 * Il controller inghiotte i propri errori e risponde `{ stato: 'reject' }`
 * con status 201: va trattato come un fallimento, non come «email libera».
 */
export async function verificaEmail(mail) {
  const { data } = await API.post('/customer/exist', { mail }, SILENZIOSO);
  if (data?.stato === 'reject') throw new Error('Verifica email non riuscita');
  return { esiste: data?.exist === true, cliente: data?.customer || null };
}

/** Aggiunge ai clienti del partner un cliente gia' registrato. Il partner lo impone il backend. */
export async function aggiungiAlPartner(mail) {
  const { data } = await API.post('/customer/add-partner', { mail }, SILENZIOSO);
  if (data?.stato === 'reject' || data?.exist !== true) {
    throw new Error('Aggiunta del cliente non riuscita');
  }
  return data.customer;
}

/**
 * Cerca l'indirizzo con lo STESSO servizio che usa la registrazione (Google
 * Maps): se qui non si trova, al salvataggio il backend rifiuterebbe il
 * cliente. Se non trova nulla il backend risponde 500.
 */
export async function geolocalizza({ address, street_number, cap, city, province }) {
  const text = `${address} ${street_number} ${cap}, ${city} ${province}`;
  const { data } = await API.post('/planner/maps-geocode', { text }, SILENZIOSO);
  return data;
}

/**
 * Registra il cliente con le sue auto.
 * Ritorna il cliente creato (con `cars[].id`), oppure null da un backend che
 * non lo restituisce ancora.
 */
export async function registraCliente(payload) {
  const { data } = await API.post('/auth/register', payload, SILENZIOSO);
  if (data?.stato === 'reject') throw new Error('Registrazione rifiutata');
  return data?.messaggio || null;
}

/**
 * I numeri del pacchetto valet, dal modello nuovo.
 *
 * `totFattura` è l'imponibile con IVA, ed è quello che si salva sul pacchetto.
 * ⚠️ `valetPerAnno` è il numero di pickup del pacchetto (nome storico): la
 * durata non lo moltiplica più, come non moltiplica il prezzo.
 */
export function calcoloPacchetto({ valetPerAnno, anni, imponibile }) {
  const pickup = Number(valetPerAnno) || 0;
  const durata = Number(anni) || 0;
  const prezzo = Number(imponibile);
  const haPrezzo = Number.isFinite(prezzo) && prezzo > 0;
  const totFattura = haPrezzo ? arrotonda(prezzo * (1 + IVA)) : null;
  return {
    totaleValet: pickup,
    totFattura,
    rata: haPrezzo && durata > 0 ? arrotonda(prezzo / (durata * 12)) : null,
    scadenza: dayjs().add(durata, 'year'),
  };
}

/**
 * Crea il pacchetto, non ancora confermato.
 *
 * I numeri arrivano da `PropostaValet` (preventivo + `numeriPacchetto`):
 *  - `valet` e `residual_valet` = valet compresi (pickup all'anno × mesi ÷ 12
 *    per difetto): sono i campi che le prenotazioni scalano;
 *  - `duration_months` e scadenza a MESI dalla vendita; `duration_years` resta
 *    valorizzato, arrotondato per eccesso, per chi legge ancora gli anni;
 *  - `imponibile` = prezzo SIMBA del pacchetto, `partner_imponibile` = prezzo
 *    del venditore al cliente (null se il listino non ce l'ha);
 *  - `valet_band_id` = la fascia del preventivo: con questa il backend
 *    ricalcola i due prezzi e i km compresi, quelli mandati da qui valgono
 *    solo come anteprima;
 *  - `included_km` = km compresi: le corse li sforano solo per i km in più.
 * Il partner lo impone il backend (`@TenantCrudAuth`), qui lo si passa per
 * completezza.
 */
export async function proponiPacchetto({
  carId, partnerId, pickup, mesi, valetTotali, totaleSimba, totaleVenditore, bandId, includedKm,
}) {
  const imponibile = arrotonda(Number(totaleSimba) || 0);
  const prezzoVenditore = totaleVenditore === null || totaleVenditore === undefined
    ? null
    : arrotonda(Number(totaleVenditore));
  const { data } = await API.post(
    '/minute',
    {
      valet: valetTotali,
      residual_valet: valetTotali,
      valet_per_year: Number(pickup),
      duration_months: Number(mesi),
      duration_years: Math.ceil(Number(mesi) / 12),
      expiration_valet: dayjs().add(Number(mesi), 'month').toDate(),
      imponibile,
      tot_fattura: arrotonda(imponibile * (1 + IVA)),
      partner_imponibile: prezzoVenditore,
      valet_band_id: bandId ?? null,
      included_km: includedKm ?? null,
      // Niente `rif_fattura`: «non fatturato» è NULL, e 0 verrebbe salvato
      // come un riferimento vero.
      esito: false,
      accepted: false,
      data: new Date(),
      car: { id: carId },
      partner: { id: partnerId },
    },
    SILENZIOSO,
  );
  return data;
}

/**
 * Preventivo di vendita del pacchetto valet.
 *
 * Distanza su strada casa del cliente → sede (una sede del partner con
 * `headquarterId`, oppure un indirizzo scritto a mano in `destination`), la
 * fascia del listino e, per 1, 2 e 3 pickup all'anno, il prezzo Simba e del
 * venditore di UN pickup all'anno (e il mensile). Il backend sceglie il listino
 * del partner o, se non ne ha uno, quello generale di Simba.
 */
export async function preventivoValet({ origin, headquarterId, destination }) {
  const { data } = await API.post(
    '/valet-km-band/quote',
    { origin, headquarterId: headquarterId || undefined, destination: destination || undefined },
    SILENZIOSO,
  );
  return data;
}

/**
 * I numeri di un pacchetto a partire da una riga del preventivo.
 *
 * ⚠️ IL PREZZO NON DIPENDE DALLA DURATA. Le tre righe del preventivo sono
 * prezzi di UN pickup per fascia di volume (uno, due, tre o più): il totale è
 * `prezzo unitario × numero di pickup`, e i mesi servono solo a calcolare la
 * scadenza. I valet del pacchetto sono i pickup comprati, tutti.
 *
 * Si calcola sia sul prezzo Simba (quello che si salva sul pacchetto) sia su
 * quello del venditore (quello che paga il cliente). Importi senza IVA.
 *
 * `pickupScelti` permette di comprarne un numero qualsiasi: sopra i tre vale
 * il prezzo unitario della terza riga.
 */
export function numeriPacchetto(opzione, mesi, pickupScelti = null) {
  const fascia = Number(opzione?.pickups_per_year) || 0;
  const pickup = Math.max(1, Math.floor(Number(pickupScelti ?? fascia) || 0));
  const m = Math.max(0, Math.floor(Number(mesi) || 0));
  const unitario = (v) => (v === null || v === undefined ? null : arrotonda(Number(v)));
  // `*_unit` è il campo nuovo; `*_annual` è il vecchio nome, stesso valore.
  const unitSimba = unitario(opzione?.simba_unit ?? opzione?.simba_annual);
  const unitVenditore = unitario(opzione?.partner_unit ?? opzione?.partner_annual);
  const totaleSimba = unitSimba === null ? null : arrotonda(unitSimba * pickup);
  const totaleVenditore = unitVenditore === null ? null : arrotonda(unitVenditore * pickup);
  // Quanto viene al mese sulla durata scelta. NON è il prezzo (che dipende
  // solo dai pickup): è la rateizzazione, quella che il cliente chiede sempre
  // davanti a un contratto di due o quattro anni.
  const alMese = (totale) => (totale === null || m <= 0 ? null : arrotonda(totale / m));
  return {
    pickup,
    mesi: m,
    valetTotali: pickup,
    unitSimba,
    unitVenditore,
    totaleSimba,
    totaleVenditore,
    mensileSimba: alMese(totaleSimba),
    mensileVenditore: alMese(totaleVenditore),
    scadenza: dayjs().add(m, 'month'),
  };
}

export default {
  IVA,
  contestoPartner,
  verificaEmail,
  aggiungiAlPartner,
  geolocalizza,
  registraCliente,
  calcoloPacchetto,
  proponiPacchetto,
  preventivoValet,
  numeriPacchetto,
};
